// BF5d-2: A BY-WEIGHT OR BY-VOLUME SHIPMENT WHOSE LINES CARRY NO DIMENSION.
//
// WHAT THIS IS FOR, AND WHAT IT IS NOT. The engine already refuses this
// outright -- engine/sourcing.py:489 raises when the chosen basis gives every
// line on a shipment a share of zero, naming the basis -- so today's failure is
// a LOUD save error, not a silent wrong number. Nothing here is a safety
// control, and nothing here may become one: the engine's refusal stays exactly
// where it is and stays the thing that actually protects the numbers.
//
// This is UX. Two things are wrong with letting the round trip happen:
//
//   1. THE PROSE IS THE ENGINE'S. "engine.sourcing: the 'By Weight' allocation
//      basis gives every line a share of zero" is precise, and is addressed to
//      whoever wrote the caller rather than to whoever is quoting.
//   2. THE REFUSAL TAKES THE WHOLE SAVE WITH IT. The controller rolls the
//      transaction back explicitly (controllers/main.py:339), so every line
//      edit in the same request dies with the allocation. And the DRAFT
//      autosave has no error surface at all -- QuoteDetail latches only
//      `quoteFinalised` and returns silently on any other `ok: false` -- so
//      this condition autosaves into a wall every 1.5s with nothing on screen.
//      That silent wall is the real defect; the manual Save error was the only
//      thing that ever revealed it.
//
// ⚠️ THIS FUNCTION MIRRORS THE ENGINE. IT MUST NEVER BE STRICTER.
//
// The engine's condition is a SUM OVER THE SHIPMENT, not a test on each line:
//
//     share_i    = qty_i x weight_i   (By Weight)
//                  qty_i x volume_i   (By Volume)
//     refuses iff sum(share_i) == 0
//
// One weighted line among nine weightless ones allocates perfectly well -- the
// weightless nine take a share of zero and the tenth carries the bill, which is
// a real answer and not an error. A guard written per-line would refuse that
// save, and refusing a save the backend would have accepted is the one way this
// slice can do damage. `freightBasisGuard.test.ts` pins that case first and
// mutation-checks it.
//
// TWO THINGS THE ENGINE DOES *NOT* GATE ON, deliberately mirrored:
//
//   * THE BILL. The zero-share check runs BEFORE `total_freight` is used, and
//     shipment.py's `_allocate_freight` does not skip a zero bill either -- its
//     own docstring calls a bill of zero "a real thing (carrier-included
//     freight)" that "does allocate". So a By-Weight shipment with no bill
//     recorded and weightless lines still raises, and this still reports it.
//   * BY VALUE / EQUAL SPLIT. By Value's share is `unit_cost x qty` and Equal
//     Split's is a literal 1, so neither can total zero across lines that
//     exist. They are outside the condition and are never reported.
//
// ONE FURTHER ENGINE REFUSAL IS OUT OF SCOPE and is not detected here:
// sourcing.py:506, a line whose goods value is zero being allocated a non-zero
// cost. Different data, different remedy, its own slice if it ever earns one.

import type { AllocationBasis, Material, QuoteShipment } from "@/types";

/** One shipment that cannot be allocated as it is currently configured. */
export interface FreightBasisProblem {
  /** The panel key, so a caller can point at the shipment it names. Never a
   *  database id -- a shipment created this session does not have one. */
  shipmentKey: string;
  /** What the panel calls it. This is what the message says out loud, because
   *  it is the only name the person reading it has ever seen. */
  shipmentName: string;
  basis: AllocationBasis;
  /** The dimension whose sum came to zero. Derived FROM the basis rather than
   *  carried alongside it, so the two cannot disagree. */
  missing: "weight" | "volume";
}

/** The bases whose share metric can total zero, and the field each one reads.
 *  Keyed off the engine's own strings (engine/sourcing.py:264-267), which are
 *  also the wire values the backend validates against its Selection keys. */
const DIMENSIONAL_BASES: Partial<Record<AllocationBasis, "weight" | "volume">> = {
  "By Weight": "weight",
  "By Volume": "volume",
};

/** Shipments in the payload that the allocator would refuse.
 *
 *  ⚠️ ITERATES THE SHIPMENTS AND MATCHES ROWS TO THEM -- never the reverse,
 *  and this is the half that survives the reconcile.
 *
 *  MaterialTable clears a line whose shipment the panel deleted, but it does so
 *  in an effect (MaterialTable.tsx:938) that runs AFTER the panel's own state
 *  change. So there is a window in which `shipments` has dropped a key and
 *  `rows` still names it. Iterating shipments means such a line matches nothing
 *  and counts toward no share -- which is right, because the payload will not
 *  put it on any shipment either. Grouping rows by `shipmentKey` first would
 *  invent a group for a shipment that is not being sent, and then need a basis
 *  for it that does not exist.
 *
 *  The same reasoning covers a line about to be unassigned by hand: while it
 *  still holds the key it names a shipment, and the moment that shipment stops
 *  being in this list it stops counting.
 *
 *  @param shipments EXACTLY the list handed to the save. The caller must pass
 *    the same value it gives `saveQuoteToBackend`/`saveDraft` -- on the quote
 *    screen that is `shipmentsRef.current`, never the render-time state, or
 *    this guards a different quote from the one that gets sent.
 *  @param rows The lines going out in the same request.
 *  @param mode The pricing mode THE PAYLOAD DECLARES. Read it off the object
 *    being sent (`quoteForSave`), not off a component variable that may have
 *    drifted from it -- that drift is a bug this codebase has already had once,
 *    and the note at QuoteDetail.tsx:302 exists because of it. A domestic
 *    payload emits no shipments block and no `shipment_key`, so the allocator
 *    never runs and there is nothing to report.
 */
export function freightBasisProblems(
  shipments: QuoteShipment[] | undefined,
  rows: Material[] | undefined,
  mode: "domestic" | "import" | undefined,
): FreightBasisProblem[] {
  if (mode !== "import") return [];
  if (!shipments || shipments.length === 0) return [];
  if (!rows || rows.length === 0) return [];

  const problems: FreightBasisProblem[] = [];

  for (const shipment of shipments) {
    const missing = DIMENSIONAL_BASES[shipment.allocationBasis];
    // By Value and Equal Split cannot total zero across lines that exist.
    if (!missing) continue;

    // TRUTHY, matching materialToBackendSaveLine's `!m.shipmentKey` and the
    // table's own reconcile. An empty-string key reads as unassigned
    // everywhere else, so it has to read as unassigned here too.
    const assigned = rows.filter((r) => r.shipmentKey && r.shipmentKey === shipment.key);

    // NO LINES, NO ALLOCATION -- and no complaint. The backend returns quietly
    // on an empty shipment (shipment.py:404) because it is an ordinary
    // transient state: a shipment created before anything is put on it. A
    // banner there would fire the instant someone pressed "Add shipment".
    if (assigned.length === 0) continue;

    // THE ENGINE'S SUM, not a per-line test. See the header.
    const totalShare = assigned.reduce(
      (sum, r) => sum + (r.qty || 0) * ((missing === "weight" ? r.weight : r.volume) || 0),
      0,
    );
    if (totalShare > 0) continue;

    problems.push({
      shipmentKey: shipment.key,
      shipmentName: shipment.name,
      basis: shipment.allocationBasis,
      missing,
    });
  }

  return problems;
}

/** The sentence both save seams say, built once so they cannot word it
 *  differently.
 *
 *  ⚠️ IT NAMES A REMEDY THAT EXISTS. The grid has no weight or volume column --
 *  those figures are copied from the catalog when a product is picked and are
 *  not editable anywhere -- so "fill in the weight" would send someone looking
 *  for a control that is not there. The two things a person can actually do are
 *  change the basis in the sourcing panel, or put lines on the shipment that
 *  carry the dimension. Editable per-line dimensions are a payload-truthiness
 *  problem and would be their own slice.
 */
export function freightBasisMessage(problems: FreightBasisProblem[]): string {
  if (problems.length === 0) return "";

  const each = problems.map(
    (p) =>
      `"${p.shipmentName}" is set to ${p.basis}, but no line on it carries a ${p.missing}`,
  );

  const subject =
    each.length === 1
      ? each[0]
      : `${each.slice(0, -1).join("; ")}; and ${each[each.length - 1]}`;

  // Singular when there is one problem, so the advice names the dimension the
  // person actually has to supply rather than offering them a choice of two.
  const dimension = problems.length === 1 ? problems[0].missing : "weight or volume";

  return (
    `${subject}. Freight cannot be split by a dimension that totals zero. ` +
    `Choose a different allocation basis in the Quote Analysis panel, or assign ` +
    `lines that carry a ${dimension}.`
  );
}
